import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useGetTaskDefinitionDetailsQuery,
  useUpdateTaskDefinitionMutation,
} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {SidebarLinkActions} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ActionForm, useActionForm} from 'src/app/actions/ActionForm';
import {
  ActionFormMetaRule,
  ActionPillConfig,
  ActionsDocumentTitle,
  MapActionFormDataToTaskDefinitionRequest,
  MapActionResponseToFormData,
} from 'src/app/actions/actionHelpers';
import {LockGuard} from 'src/app/lock-mgt/LockGuard';
import {TaskStateBanner} from 'src/app/tasks/TaskStateBanner';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachine} from 'src/types/StateMachine';
import {useActionsBreadcrumbs} from '../hooks/useActionsBreadcrumbs';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const EditActionPage: React.FC = () => {
  useSetDocumentTitle(ActionsDocumentTitle);
  const featureFlag = FeatureFlagEnum.Actions;
  const taskType = TaskTypeEnum.Actions;
  const module = ModuleEnum.Actions;

  const {setAlert} = useAlert();
  const {taskId} = useParams();

  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const parsedTaskId =
    taskId !== undefined && !isNaN(+taskId) ? +taskId : undefined;

  const {
    data: taskDefinitionData,
    isLoading: isLoadingTask,
    error: taskLoadError,
  } = useGetTaskDefinitionDetailsQuery(parsedTaskId!, {
    skip: parsedTaskId === undefined,
  });

  const [updateTaskDefinition] = useUpdateTaskDefinitionMutation();

  const isLoading = isLoadingTask;

  const editItem = useMemo(
    () =>
      taskDefinitionData
        ? MapActionResponseToFormData(taskDefinitionData)
        : undefined,
    [taskDefinitionData]
  );

  const {
    formApi,
    formMeta,
    isLoading: isActionFormLoading,
  } = useActionForm({
    metarules: [ActionFormMetaRule.UseDueDate],
    taskState: editItem?.state,
  });
  const {resetForm} = formApi;

  useEffect(() => {
    if (taskLoadError) {
      setAlert(parseErrorMessage(taskLoadError), 'error');
    }
  }, [taskLoadError, setAlert]);

  const {backBarProps} = useActionsBreadcrumbs({
    mode: 'edit',
    item: editItem,
  });
  const {returnTo} = useBackNavigation(backBarProps.fallbackTo);

  const submit = async () => {
    if (!(await formApi.validateForm())) {
      return {continue: false};
    }

    setIsSavePending(true);

    const body = MapActionFormDataToTaskDefinitionRequest(
      formApi.methods.getValues(),
      taskType,
      undefined
    );

    try {
      await updateTaskDefinition(body).unwrap();

      setAlert(`Action was successfully updated`, 'success');
      return {continue: true};
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
      return {continue: false};
    }
  };

  const onSubmitted = () =>
    returnTo(PathUtility.ActionView(taskId as string, 'content'));

  const handleSave = async () => {
    if (await formApi.validateForm()) {
      await submit();
      onSubmitted();
    }
  };

  useEffect(() => {
    if (formMeta.length > 0 && editItem) {
      resetForm(toFormDataV2(editItem));
    }
  }, [formMeta, editItem, resetForm]);

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <ContentWrapper featureFlag={featureFlag}>
      <BackBar {...backBarProps} />
      {!isLoading && editItem ? (
        <Stack>
          <PageHeader>
            <PageHeader.Title
              icon={SidebarLinkActions.icon}
              subtitle={editItem.internalIdentifier}>
              {editItem.subject}
            </PageHeader.Title>

            <PageHeader.Badges>
              <PageHeader.Badge color="yellow" variant="light">
                Editing
              </PageHeader.Badge>
              {ActionPillConfig(
                editItem.state,
                undefined,
                undefined,
                editItem.dueAt
              ).map((pill, index) => (
                <PageHeader.Badge
                  key={index}
                  color={pill.color}
                  variant="light">
                  {pill.text}
                </PageHeader.Badge>
              ))}
            </PageHeader.Badges>
          </PageHeader>

          <TaskStateBanner module={module} state={editItem.state} />

          <LockGuard
            id={editItem.id}
            type={EntityIdentifierEnum.TaskDefinition}
            lockCache={editItem.locked}>
            <ActionForm form={formApi} isLoading={isActionFormLoading} />

            <Group align="start">
              {machine && (
                <FormTransitionBar
                  preTransitionButtonNodes={[
                    <SaveButton
                      loading={isSavePending}
                      onClick={handleSave}
                      mr="md"
                    />,
                  ]}
                  onTransitioning={submit}
                  module={module}
                  stateMachine={machine}
                  stateId={editItem.state}
                  transitionBaseUrl={`task-definition/${editItem.id}/transition`}
                  onRefreshTransition={onSubmitted}
                  bypassPermissions={true}
                  invalidateTagsOnComplete={[
                    'TaskDefinitions',
                    'TaskAssignments',
                  ]}
                />
              )}
            </Group>
          </LockGuard>
        </Stack>
      ) : (
        <TableBodyRender
          isLoading={isLoading}
          hasData={editItem !== undefined}
        />
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.ActionEdit(':taskId'),
  element: <EditActionPage />,
  isPrivate: true,
});

export default EditActionPage;
